import { Product } from '../../../../models/product';
import { RuleLevel } from './rule-level';
import { Utils } from '../../../../../core/services/utils.service';
import { EquipmentBase } from '../../../../models/equipment-base';
import { ITreeProperty } from '../../../../models/product-base';

export type GroupType = '' | 'brand' | 'category' | 'supplier' | 'characteristic' | 'shelf';

type NamesParams = Map<string, string>;

export type Names = {
    category: NamesParams,
    brand: NamesParams,
    supplier: NamesParams,
    characteristic: NamesParams
}

export const GROUPING = () => {
    return [
        {
            value: '',
            title: 'DONT_GROUP',
            disabled: false,
            opened: false,
            children: []
        },
        {
            value: 'category',
            title: 'CATEGORIES',
            disabled: false,
            opened: false,
            children: []
        },
        {
            value: 'brand',
            title: 'BRANDS',
            disabled: false,
            opened: false,
            children: [],
            id: (product: Product) => product.brandsUniqueId
        },
        {
            value: 'supplier',
            title: 'SUPPLIERS',
            disabled: false,
            opened: false,
            children: [],
            id: (product: Product) => product.suppliersUniqueId
        },
        {
            value: 'characteristic',
            title: 'CHARACTERISTIC',
            disabled: false,
            opened: false,
            children: []
        },
        {
            value: 'shelf',
            title: 'RULES.GROUP.SHELVES',
            disabled: false,
            opened: false,
            children: []
        }
    ]
};

interface Group {
    readonly type: GroupType;
    readonly extendedType: string;
    readonly isHorizontal: boolean;
    group(products: Array<Product>, level?: RuleLevel, stand?: EquipmentBase): Array<{id: string, products: Array<Product>}>;
    getChildrenLevels(products: Array<Product>, level?: RuleLevel, stand?: EquipmentBase): Array<string>;
    getChildrenName(id: string, names?: Names): string;
}

export class GroupBase implements Group {
    readonly type: GroupType;
    readonly extendedType: string;
    readonly isHorizontal: boolean = false;

    constructor(type: GroupType, extendedType: string) {
        this.type = type;
        this.extendedType = extendedType;
    }

    group(products: Array<Product>, level?: RuleLevel, stand?: EquipmentBase): Array<{id: string, products: Array<Product>}> {
        return [{id: '', products: products}];
    }

    getChildrenLevels(products: Array<Product>, level?: RuleLevel, stand?: EquipmentBase): Array<string> {
        return [];
    }

    getChildrenName(id: string, names?: Names): string {
        return id;
    }
}

export class GroupByKey extends GroupBase {
    constructor(type: GroupType) {
        super(type, '');
    }

    group(products: Array<Product>): Array<{id: string, products: Array<Product>}> {
        const groups = {};
        const grouping = GROUPING().find((group) => group.value === this.type);

        products.forEach(product => {
            const id: number | string = grouping?.id(product) ?? product[this.type].id ?? '';
            if (groups.hasOwnProperty(id)) {
                groups[id].push(product);
            } else {
                groups[id] = [product];
            }
        });

        return Object.keys(groups).map(key => {
            return {id: key.toString(), products: groups[key]};
        });
    }

    getChildrenLevels(products: Array<Product>): Array<string> {
        if (!products.length) return [''];

        const groups = [];
        const grouping = GROUPING().find((group) => group.value === this.type);

        products.forEach(product => {
            const id: number | string = grouping?.id(product) ?? product[this.type].id ?? '';
            groups.push(id);
        });

        return Utils.unique(groups).map(level => level.toString());
    }

    getChildrenName(id: string, names: Names): string {
        return names[this.type].get(String(id)) || '';
    }
}

export class GroupByCategory extends GroupBase {
    constructor(type: GroupType, extendedType: string) {
        super(type, extendedType);
    }

    group(products: Array<Product>): Array<{id: string, name: string, products: Array<Product>}> {
        const levels: {[key: string]: { name: string, products: Array<Product> }} = {};

        products.forEach((product: Product) => {
            let selectedCategories: Array<ITreeProperty> = product.categories
                .map((categories: Array<ITreeProperty>) => categories.slice(1))
                .filter((categories: Array<ITreeProperty>) => categories.length)
                .map((categories: Array<ITreeProperty>) => {
                    let category: ITreeProperty, index: number = +this.extendedType + 1;
                    while (!category && index > 0) {
                        category = categories.find((c: ITreeProperty) => c.level == index);
                        index--;
                    }
                    return category || categories.reduce((max, cat) => cat.level > max.level ? cat : max, { level: -1, name: '', id: null });
                });

            const key: string = Utils
                .unique<number>(selectedCategories.map((c: ITreeProperty) => c.id))
                .sort((a, b) => a - b)
                .join('-');
            const name: string = Utils
                .unique(selectedCategories.map((c: ITreeProperty) => c.name))
                .sort()
                .join('; ');

            if (levels.hasOwnProperty(key)) {
                levels[key].products.push(product);
            } else {
                levels[key] = {
                    products: [product],
                    name: name
                };
            }
        });

        return Object.keys(levels).map(key => {
            return { id: key, name: levels[key].name, products: levels[key].products };
        });
    }

    getChildrenLevels(products: Array<Product>): Array<string> {
        const levels = new Set<string>();

        products.forEach((product: Product) => {
            let selectedCategories: Array<ITreeProperty> = product.categories
                .map((categories: Array<ITreeProperty>) => categories.slice(1))
                .filter((categories: Array<ITreeProperty>) => categories.length)
                .map((categoryArray: Array<ITreeProperty>) => {
                    let category: ITreeProperty, index: number = +this.extendedType + 1;
                    while (!category && index > 0) {
                        category = categoryArray.find((cat: ITreeProperty) => cat.level == index);
                        index--;
                    }
                    return category || categoryArray.reduce((max, cat) => cat.level > max.level ? cat : max, { level: -1, name: '', id: null });
                });

            const key: string = Utils
                .unique<number>(selectedCategories.map((c: ITreeProperty) => c.id))
                .sort((a, b) => a - b)
                .join('-');

            selectedCategories.forEach((category: ITreeProperty) => {
                if (category) levels.add(key);
            });
        });

        return Array.from(levels);
    }

    getChildrenName(id: string, names: Names): string {
        return names[this.type].get(String(id)) || '';
    }
}

export class GroupByCharacteristic extends GroupBase {
    constructor(type: GroupType, extendedType: string) {
        super(type, extendedType);
    }

    group(products: Array<Product>): Array<{id: string, products: Array<Product>}> {
        const filteredProducts = products.filter(product => Utils.to1DArray(product.markers).some(ch => ch.id == +this.extendedType)),
            childrenLevels = Utils.to1DArray(filteredProducts.map(product => product.markers))
                .filter(characteristic => characteristic.parent_id == +this.extendedType);

        if (childrenLevels.length) {
            return Utils.unique(childrenLevels.map(ch => ch.id)).map(key => {
                return {id: key.toString(), products: filteredProducts.filter(product => Utils.to1DArray(product.markers).some(ch => ch.id == +key))};
            });
        }

        return [{id: this.extendedType, products: filteredProducts}];
    }

    getChildrenLevels(products: Array<Product>): Array<string> {
        const filteredProducts = products.filter(product => Utils.to1DArray(product.markers).some(ch => ch.id == +this.extendedType)),
            childrenLevels = Utils.to1DArray(filteredProducts.map(product => product.markers))
                .filter(characteristic => characteristic.parent_id == +this.extendedType);

        if (childrenLevels.length) {
            return Utils.unique(childrenLevels.map(ch => ch.id));
        }

        return [this.extendedType];
    }

    getChildrenName(id: string, names: Names): string {
        return names[this.type].get(String(id)) || '';
    }
}

export class GroupByShelf extends GroupBase {
    readonly isHorizontal: boolean = true;

    constructor(type: GroupType) {
        super(type, '');
    }

    group(products: Array<Product>, level: RuleLevel, stand: EquipmentBase): Array<{id: string, products: Array<Product>}> {
        const groups = level.children.map(child => ({ id: child.id, products: child.group.type ? products : [] })),
            allGroups = this.getChildrenLevels(products, level, stand);

        allGroups.forEach(id => {
            if (groups.every(group => !group.id.includes(id))) groups.push({ id, products: [] });
        });

        return groups;
    }

    getChildrenLevels(products: Array<Product>, level: RuleLevel, stand: EquipmentBase): Array<string> {
        let from = 0, to = stand.Width, currentLevel = level, percent = 0;

        while (currentLevel.parent) {
            const index = currentLevel.parent.children.indexOf(currentLevel);
            percent = percent + currentLevel.parent.children
                .filter((child, idx) => idx < index)
                .reduce((sum, child) => Utils.round(sum + child.calculatedPercent, 3), 0);
            currentLevel = currentLevel.parent;
        }

        if (level.parent) {
            from = Utils.round(stand.Width * percent / 100);
            to = Utils.round(stand.Width * (percent + level.calculatedPercent) / 100);
        }

        const shelves = stand.availableElements.filter(element => {
            const boundings = element.getBoundingX();
            return ['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf'].includes(element.parent.type) &&
                (
                    boundings.min >= from && boundings.min < to ||
                    boundings.max > from && boundings.max <= to ||
                    element.shapes[0].isPointOn(from) ||
                    element.shapes[0].isPointOn(to)
                )
        });

        return Utils.unique(shelves.map(element => Utils.getElementIndex(element, stand).toString())).sort();
    }
}

export class GroupFactory {
    static types = {
        '': GroupBase,
        'category': GroupByCategory,
        'brand': GroupByKey,
        'supplier': GroupByKey,
        'characteristic': GroupByCharacteristic,
        'shelf': GroupByShelf
    }

    static getGroup(type: GroupType = '', extended: string = ''): GroupBase {
        return new GroupFactory.types[type.toString()](type, extended);
    }
}